/* Predictive mobile search. History stays in this browser and can be cleared. */
(() => {
  if (customElements.get('park-mobile-search')) return;
  class ParkMobileSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.form.querySelector('[name="q"]');
      this.panel = this.querySelector('.park-search-panel');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.clear = this.querySelector('.park-search-clear');
      this.key = 'park-search-history-v1';
      this.cache = new Map();
      this.sequence = 0;
      this.events = new AbortController();
      const on = (el, name, fn) => el.addEventListener(name, fn, { signal: this.events.signal });
      on(this.input, 'focus', () => this.open());
      on(this.input, 'click', () => { if (this.panel.hidden) this.open(); });
      on(this.input, 'input', () => { this.open(false); this.queue(); });
      on(this.form, 'submit', () => { this.remember(this.input.value); this.input.blur(); this.close(); });
      on(this.clear, 'click', () => { this.input.value = ''; this.input.focus(); this.queue(); });
      on(this.querySelector('[data-search-close]'), 'click', () => this.close(true));
      on(this, 'keydown', event => {
        if (event.key === 'Escape' && !this.panel.hidden) { event.preventDefault(); event.stopPropagation(); this.close(true); }
        if (event.key === 'ArrowDown' && event.target === this.input && !this.panel.hidden) {
          const first = this.panel.querySelector('[data-search-results] a,[data-search-results] button,park-salon-search a');
          if (first) { event.preventDefault(); first.focus(); }
        }
      });
      on(this, 'focusout', () => setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0));
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.close(); });
      on(this.results, 'click', event => {
        const link = event.target.closest('a');
        if (link) this.remember(this.input.value);
      });
      on(window, 'resize', () => { this.syncSticky(); this.size(); });
      on(window, 'scroll', () => this.size());
      this.spacer = document.createElement('div');
      this.spacer.className = 'park-search-spacer';
      this.spacer.setAttribute('aria-hidden', 'true');
      this.closest('.section-header')?.after(this.spacer);
      this.stickyObserver = new MutationObserver(() => this.syncSticky());
      this.stickyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      const pageHeader = document.getElementById('pageheader');
      if (pageHeader) this.stickyObserver.observe(pageHeader, { attributes: true, attributeFilter: ['class'] });
      this.syncSticky();
      if (window.visualViewport) {
        on(visualViewport, 'resize', () => this.size());
        on(visualViewport, 'scroll', () => this.size());
      }
    }
    disconnectedCallback() { this.spacer?.remove(); this.stickyObserver?.disconnect(); cancelAnimationFrame(this.stickyFrame); this.events?.abort(); this.cancel(); document.body.classList.remove('park-search-open'); }
    syncSticky() {
      const collapsed = innerWidth >= 1024 && !!document.getElementById('pageheader')?.classList.contains('park-header--compact');
      if (!collapsed && innerWidth >= 768 && this.getBoundingClientRect().height) {
        this.spacer.style.height = `${this.getBoundingClientRect().height}px`;
      }
      if (collapsed === this.stickyCollapsed) return;
      this.stickyCollapsed = collapsed;
      if (collapsed) {
        if (this.contains(document.activeElement)) document.activeElement.blur();
        this.close();
      }
      cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = requestAnimationFrame(() => {
        const header = this.closest('.section-header');
        if (!header || !this.isConnected) return;
        const height = Math.ceil(header.clientHeight);
        const sticky = getComputedStyle(header).position === 'sticky';
        document.documentElement.style.setProperty('--theme-header-height', `${height}px`);
        document.documentElement.style.setProperty('--theme-sticky-header-height', `${sticky ? height : 0}px`);
      });
    }
    cancel() { clearTimeout(this.timer); this.request?.abort(); this.sequence++; }
    open(refresh = true) {
      if (this.restoringFocus) return;
      const wasClosed = this.panel.hidden;
      this.panel.hidden = false;
      this.prepareSalons();
      this.input.setAttribute('aria-expanded', 'true');
      document.body.classList.add('park-search-open');
      this.size();
      if (refresh && wasClosed) this.queue();
    }
    prepareSalons() {
      this.salons = this.querySelector('park-salon-search');
      if (!this.salons) return;
      if (!document.querySelector('[data-park-salon-style]')) {
        const style = document.createElement('link'); style.rel = 'stylesheet'; style.fetchPriority = 'low'; style.href = this.dataset.salonStyle; style.dataset.parkSalonStyle = ''; document.head.append(style);
      }
      if (!customElements.get('park-salon-search') && !document.querySelector('[data-park-salon-script]')) {
        const script = document.createElement('script'); script.src = this.dataset.salonScript; script.async = true; script.fetchPriority = 'low'; script.dataset.parkSalonScript = ''; script.onerror = () => script.remove(); document.head.append(script);
      }
    }
    close(restore = false) {
      this.cancel();
      this.panel.hidden = true;
      this.panel.removeAttribute('aria-busy');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('park-search-open');
      if (restore) {
        this.restoringFocus = true;
        this.input.focus({ preventScroll: true });
        this.restoringFocus = false;
      }
    }
    size() {
      if (this.panel.hidden) return;
      const bottom = visualViewport ? visualViewport.height + visualViewport.offsetTop : innerHeight;
      this.style.setProperty('--park-search-height', `${Math.max(0, bottom - this.panel.getBoundingClientRect().top)}px`);
    }
    history() {
      try {
        const entries = JSON.parse(localStorage.getItem(this.key) || '[]');
        return Array.isArray(entries) ? entries.filter(x => typeof x === 'string' && x.length <= 100).slice(0, 5) : [];
      } catch { return []; }
    }
    remember(value) {
      const term = value.trim().slice(0, 100);
      if (!term) return;
      try { localStorage.setItem(this.key, JSON.stringify([term, ...this.history().filter(x => x.toLocaleLowerCase() !== term.toLocaleLowerCase())].slice(0, 5))); } catch { /* Search works without storage. */ }
    }
    recent() {
      this.results.replaceChildren();
      this.status.textContent = '';
      const terms = this.history();
      if (!terms.length) { const p = document.createElement('p'); p.textContent = this.dataset.empty; this.results.append(p); return; }
      const head = document.createElement('div'); head.className = 'park-search-history-head';
      const title = document.createElement('h2'); title.className = 'park-search-heading'; title.textContent = this.dataset.recent;
      const clear = document.createElement('button'); clear.type = 'button'; clear.textContent = this.dataset.clear;
      clear.addEventListener('click', () => { try { localStorage.removeItem(this.key); } catch {} this.recent(); this.input.focus(); });
      head.append(title, clear);
      const list = document.createElement('ul'); list.className = 'park-search-history';
      terms.forEach(term => {
        const li = document.createElement('li'), button = document.createElement('button');
        button.type = 'button'; button.textContent = term;
        button.addEventListener('click', () => { this.input.value = term; this.input.focus(); this.queue(); });
        li.append(button); list.append(li);
      });
      this.results.append(head, list);
    }
    queue() {
      this.cancel();
      const term = this.input.value.trim().slice(0, 100);
      if (this.salons) this.salons.dataset.query = term;
      this.clear.hidden = !this.input.value;
      this.panel.removeAttribute('aria-busy');
      if (!term) { this.recent(); return; }
      this.results.replaceChildren();
      this.status.textContent = this.dataset.loading;
      if (this.cache.has(term)) { this.render(this.cache.get(term)); return; }
      this.panel.setAttribute('aria-busy', 'true');
      const sequence = this.sequence;
      this.timer = setTimeout(() => this.search(term, sequence), 100);
    }
    async search(term, sequence) {
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL(this.dataset.endpoint, location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', 'park-predictive-search');
        url.searchParams.set('resources[type]', 'product,query,collection');
        url.searchParams.set('resources[limit]', '5');
        url.searchParams.set('resources[limit_scope]', 'each');
        // Main navigation establishes the preview cookie. Adding preview_theme_id
        // here makes Shopify's redirect flatten the nested resources parameters.
        const response = await fetch(url, { signal: controller.signal, priority: 'high' });
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (sequence !== this.sequence || this.panel.hidden) return;
        const result = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-park-predictive-results]');
        if (!result) throw new Error('Missing search results');
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(term, result.outerHTML);
        this.render(result.outerHTML);
      } catch {
        if (sequence !== this.sequence || this.panel.hidden) return;
        this.status.textContent = '';
        const p = document.createElement('p'); p.textContent = this.dataset.error; this.results.replaceChildren(p);
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) this.panel.removeAttribute('aria-busy');
      }
    }
    render(html) {
      this.results.innerHTML = html;
      const preview = this.form.elements.preview_theme_id?.value;
      this.results.querySelectorAll('a[href]').forEach(link => {
        const url = new URL(link.getAttribute('href'), location.origin);
        if (url.origin === location.origin && preview) { url.searchParams.set('preview_theme_id', preview); link.href = url.href; }
      });
      this.status.textContent = this.results.firstElementChild?.dataset.count || '';
      this.panel.removeAttribute('aria-busy');
    }
  }
  customElements.define('park-mobile-search', ParkMobileSearch);
})();
